import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Input, Label, Larger, Textarea } from '@/components/Forms';
import { NewDropdown } from '@/components/Forms/Dropdown';
import {
  Dropzone,
  InnerLabel,
  Notice,
  Title,
} from '@/components/Forms/FileField';
import FormField from '@/components/Forms/FormField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import InfoTooltip from '@/components/InfoTooltip';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAccountNumbersOptions from '@/hooks/useAccountNumbersOptions';
import useAlerts from '@/hooks/useAlerts';
import useCompaniesOptions from '@/hooks/useCompaniesOptions';
import useDepartmentsOptions from '@/hooks/useDepartmentsOptions';
import GpmCheckboxFiekd from '@/pages/AddInvoice/components/GpmCheckboxFiekd';
import ProformaCheckboxField from '@/pages/AddInvoice/components/ProformaCheckboxField';
import { InvoiceResource } from '@/types/resources';
import { api } from '@/utils/api';
import { currencies } from '@/utils/data';
import { handleFormErrors } from '@/utils/helpers';

export type FormDataT = {
  external_id: string;
  department_id: number | null;
  company_id: number | null;
  finance_account_number_id: number | null;
  currency: 'USD' | 'EUR' | 'GBP';
  amount: number;
  file: File | null;
  proforma_file: File | null;
  is_proforma: boolean;
  comment: string;
  paypal_email: string;
  payment_purpose: string;
  gpm: boolean;
  serial_number: string;
  invoice_number: string;
  invoice_date: string;
};

const StyledInput = styled(Input)`
  border-bottom-right-radius: 0;
  border-top-right-radius: 0;
  padding-left: 34px;
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const InputHolder = styled.div`
  position: relative;
  width: 100%;
`;

const Currency = styled(Text)`
  position: absolute;
  left: 16px;
  top: 12px;
`;

const StyledModal = styled(Modal)<{ $noBackdrop?: boolean }>`
  ${({ $noBackdrop }) =>
    $noBackdrop &&
    css`
      background-color: transparent;
    `}
`;

const NewFilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const NewFileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
`;

const NewFileName = styled(Text)`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
`;

const RemoveFileButton = styled.button`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gray};
  flex-shrink: 0;
  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
};

const ADDITIONAL_FILE_ACCEPT =
  '.doc, .docx,application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/pdf';

const MAX_ADDITIONAL_FILE_SIZE = 5 * 1024 * 1024;

const RemoveIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 3L3 9M3 3L9 9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

interface Props {
  onClose: () => void;
  invoice: InvoiceResource;
  noBackdrop?: boolean;
}

const InvoicesEditModal: FC<Props> = ({ onClose, invoice, noBackdrop }) => {
  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const [additionalFiles, setAdditionalFiles] = useState<File[]>([]);
  const additionalFilesInputRef = useRef<HTMLInputElement>(null);
  const [removedPairIds, setRemovedPairIds] = useState<string[]>([]);

  const [isFinalFileRemoved, setIsFinalFileRemoved] = useState(false);

  const [isProformaFileRemoved, setIsProformaFileRemoved] = useState(false);

  const displayAdditionalFiles = useMemo(() => {
    const pdfPairIds = new Set(
      (invoice.additional_files ?? [])
        .map((f) => f.meta?.pair_id)
        .filter(Boolean),
    );
    return [
      ...(invoice.additional_files ?? []),
      ...(invoice.original_additional_files ?? []).filter(
        (f) => !pdfPairIds.has(f.meta?.pair_id),
      ),
    ];
  }, [invoice.additional_files, invoice.original_additional_files]);

  const handleAdditionalFilesChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!e.target.files) return;
    const valid = Array.from(e.target.files).filter(
      (f) => f.size <= MAX_ADDITIONAL_FILE_SIZE,
    );
    setAdditionalFiles((prev) => [...prev, ...valid]);
    e.target.value = '';
  };

  const { data: accountNumberOptions = [] } = useAccountNumbersOptions();

  const { data: departmentOptions = [] } = useDepartmentsOptions();

  const filteredDepartmentOptions = departmentOptions.filter(
    (dept) => !dept.hide_from_dropdowns,
  );

  const { data: allCompanyOptions = [] } = useCompaniesOptions();

  const companyOptions = allCompanyOptions.filter(
    (company) => !company.hide_from_dropdowns,
  );

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      external_id: invoice?.external_id ?? '',
      amount: invoice?.amount ?? '',
      currency: invoice?.currency ?? '',
      department_id: invoice?.department_id ?? null,
      company_id: invoice?.company_id ?? null,
      file: null,
      proforma_file: null,
      is_proforma: invoice.is_proforma,
      finance_account_number_id: invoice.finance_account_number_id ?? null,
      comment: invoice.comment ?? '',
      gpm: invoice.gpm,
      payment_purpose: invoice.payment_purpose ?? '',
    },
  });

  const currentCurrency = useWatch({
    control,
    name: 'currency',
  });

  const isGpm = useWatch({
    name: 'gpm',
    control,
  });

  const isProforma = useWatch({
    name: 'is_proforma',
    control,
  });

  const currentCurrencySymbol = CURRENCY_SYMBOLS[currentCurrency] ?? '€';

  // An invoice added with its file as the final one can still become a proforma:
  // the file it already has becomes the proforma document.
  const movesFileToProforma =
    isProforma &&
    !invoice.is_proforma &&
    !!invoice.file?.id &&
    !invoice.proforma_file?.id &&
    !isFinalFileRemoved;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async ({ ...data }) => {
      try {
        const formData = new FormData();

        formData.append('external_id', data.external_id);
        if (data.finance_account_number_id) {
          formData.append(
            'finance_account_number_id',
            data.finance_account_number_id.toString(),
          );
        }
        formData.append('currency', data.currency);
        formData.append('amount', data.amount.toString());

        if (data.department_id) {
          formData.append('department_id', data.department_id.toString());
        }

        if (data.company_id) {
          formData.append('company_id', data.company_id.toString());
        }

        if (data.payment_purpose) {
          formData.append('payment_purpose', data.payment_purpose);
        }

        if (data.gpm) {
          formData.append('gpm', '1');
        } else {
          formData.append('gpm', '0');
        }

        formData.append('is_proforma', data.is_proforma ? '1' : '0');

        if (movesFileToProforma) {
          formData.append('move_file_to_proforma', '1');
        } else if (data.proforma_file) {
          formData.append('proforma_file', data.proforma_file);
        } else if (isProformaFileRemoved) {
          formData.append('remove_proforma_file', '1');
        }

        if (data.file) {
          formData.append('file', data.file);
        } else if (isFinalFileRemoved && !movesFileToProforma) {
          formData.append('remove_file', '1');
        }

        additionalFiles.forEach((file) => {
          formData.append('additional_files[]', file);
        });

        const keptPairIds = new Set(
          displayAdditionalFiles
            .map((f) => f.meta?.pair_id)
            .filter((id): id is string => !!id && !removedPairIds.includes(id)),
        );
        keptPairIds.forEach((id) => formData.append('keep_pair_ids[]', id));

        // An empty keep list never reaches the backend, so say explicitly that
        // the list is complete; otherwise removing every file would keep them all.
        formData.append('sync_additional_files', '1');

        formData.append('comment', data.comment);

        formData.append('_method', 'PUT');

        await api.post(`/v1/payments/invoices/${invoice.id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        queryClient.invalidateQueries(['invoices_members'], {
          exact: true,
        });

        queryClient.invalidateQueries(['invoices_list'], {
          exact: false,
        });

        onClose();

        return alerts.success('Invoice has been edited succesfully');
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts);
      }
    })(e);
  };

  return (
    <StyledModal $noBackdrop={noBackdrop}>
      <SidePanel heading={'Edit Invoice'} onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <Label htmlFor="department_id">Department</Label>
                <NewDropdown
                  id="department_id"
                  name="department_id"
                  options={filteredDepartmentOptions}
                  control={control}
                />
                {!!errors.department_id && (
                  <ValidationMessage>
                    {errors.department_id.message}
                  </ValidationMessage>
                )}
              </Larger>
              <Larger>
                <Label htmlFor="company_id">Company</Label>
                <NewDropdown
                  id="company_id"
                  name="company_id"
                  options={companyOptions}
                  control={control}
                />
                {!!errors.company_id && (
                  <ValidationMessage>
                    {errors.company_id.message}
                  </ValidationMessage>
                )}
              </Larger>
              <Larger>
                <Label htmlFor="financeAccountNumber">
                  Finance account number
                  <InfoTooltip
                    title="Finance account number"
                    content="Accounting code that indicates the type of expenses"
                    position="bottom-center"
                  />
                </Label>
                <NewDropdown
                  id="financeAccountNumber"
                  name="finance_account_number_id"
                  options={accountNumberOptions}
                  control={control}
                />
                {!!errors.finance_account_number_id && (
                  <ValidationMessage>
                    {errors.finance_account_number_id.message}
                  </ValidationMessage>
                )}
              </Larger>
              <FormField<FormDataT>
                id="invoiceId"
                control={control}
                name="external_id"
                label="Invoice ID"
              />
              <Larger>
                <Label>Currency</Label>
                <NewDropdown
                  options={currencies}
                  control={control}
                  name="currency"
                />
                {!!errors.currency && (
                  <ValidationMessage>
                    {errors.currency.message}
                  </ValidationMessage>
                )}
              </Larger>
              <Larger>
                <Label htmlFor="amount">Amount</Label>
                <InputHolder>
                  <StyledInput
                    id="amount"
                    placeholder="Add amount"
                    {...register('amount')}
                    $hasError={!!errors.amount}
                  />
                  <Currency $type="captionSemibold">
                    {currentCurrencySymbol}
                  </Currency>
                </InputHolder>
                {!!errors.amount && (
                  <ValidationMessage>{errors.amount.message}</ValidationMessage>
                )}
              </Larger>
              <Larger>
                <Label htmlFor="paymentPurpose">The Purpose of Payment</Label>
                <Input
                  id="paymentPurpose"
                  placeholder="Add purpose"
                  {...register('payment_purpose')}
                  $hasError={!!errors.payment_purpose}
                />
                {!!errors.payment_purpose && (
                  <ValidationMessage>
                    {errors.payment_purpose.message}
                  </ValidationMessage>
                )}
              </Larger>
              <Larger>
                <GpmCheckboxFiekd isGpm={isGpm} setValue={setValue} />
              </Larger>
              <ProformaCheckboxField
                isProforma={isProforma}
                setValue={setValue}
                disabledReason={
                  !invoice.is_proforma &&
                  !!invoice.file?.id &&
                  !!invoice.proforma_file?.id &&
                  !isFinalFileRemoved
                    ? 'Already has a final invoice file'
                    : undefined
                }
              />
              {(isProforma || !!invoice.proforma_file?.id) && (
                <FormField<FormDataT>
                  // Remounted so the field picks up the file that moves in or out.
                  key={movesFileToProforma ? 'moved-file' : 'proforma-file'}
                  id="proforma_file"
                  type="file"
                  control={control}
                  name="proforma_file"
                  existingMedia={
                    movesFileToProforma ? invoice.file : invoice.proforma_file
                  }
                  onRemoveExisting={() => {
                    if (movesFileToProforma) {
                      setIsFinalFileRemoved(true);
                    } else {
                      setIsProformaFileRemoved(true);
                    }
                  }}
                  label="Proforma file"
                  notes="PDF, doc, docx, xls, xlsx / Size: Up to 2MB"
                  accept=".doc, .docx,application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/pdf"
                />
              )}
              <FormField<FormDataT>
                key={movesFileToProforma ? 'moved-file' : 'file'}
                id="file"
                type="file"
                control={control}
                name="file"
                existingMedia={
                  movesFileToProforma || isFinalFileRemoved
                    ? undefined
                    : invoice.file
                }
                onRemoveExisting={() => {
                  setIsFinalFileRemoved(true);

                  // Without its final file, an invoice with a proforma document
                  // goes back to being a proforma; the backend does the same.
                  if (invoice.proforma_file?.id && !isProformaFileRemoved) {
                    setValue('is_proforma', true);
                  }
                }}
                label={isProforma ? 'Final invoice file' : 'File'}
                notes="PDF, doc, docx, xls, xlsx / Size: Up to 2MB"
                accept=".doc, .docx,application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/pdf"
              />
              <Larger>
                <Label>Additional files</Label>
                <Dropzone
                  $dragging={false}
                  onClick={() => additionalFilesInputRef.current?.click()}
                  style={{ cursor: 'pointer' }}
                >
                  <input
                    ref={additionalFilesInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    accept={ADDITIONAL_FILE_ACCEPT}
                    onChange={handleAdditionalFilesChange}
                  />
                  <Title $type="smallBold" $color="gray" as="div">
                    Drop files here or{' '}
                    <InnerLabel $type="smallBold" $color="purple" as="span">
                      browse
                    </InnerLabel>
                  </Title>
                  <Notice $type="extraSmall" $color="gray" as="div">
                    PDF, doc, docx, xls, xlsx / Size: Up to 5MB each
                  </Notice>
                </Dropzone>
                {(!!displayAdditionalFiles.length ||
                  additionalFiles.length > 0) && (
                  <NewFilesList>
                    {displayAdditionalFiles
                      .filter(
                        (file) =>
                          !removedPairIds.includes(file.meta?.pair_id ?? ''),
                      )
                      .map((file) => (
                        <NewFileItem key={file.id}>
                          <NewFileName $type="smallMedium">
                            {file.name}
                          </NewFileName>
                          <RemoveFileButton
                            type="button"
                            onClick={() => {
                              if (file.meta?.pair_id) {
                                setRemovedPairIds((prev) => [
                                  ...prev,
                                  file.meta!.pair_id,
                                ]);
                              }
                            }}
                          >
                            <RemoveIcon />
                          </RemoveFileButton>
                        </NewFileItem>
                      ))}
                    {additionalFiles.map((file, index) => (
                      <NewFileItem key={index}>
                        <NewFileName $type="smallMedium">
                          {file.name}
                        </NewFileName>
                        <RemoveFileButton
                          type="button"
                          onClick={() =>
                            setAdditionalFiles((prev) =>
                              prev.filter((_, i) => i !== index),
                            )
                          }
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9 3L3 9M3 3L9 9"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        </RemoveFileButton>
                      </NewFileItem>
                    ))}
                  </NewFilesList>
                )}
              </Larger>
              {!!invoice.freelancer_notes && (
                <Larger>
                  <Label htmlFor="freelancerNotes">Freelancer notes</Label>
                  {/* Written by the freelancer on submission — shown here for
                      context, not ours to change. */}
                  <Textarea
                    id="freelancerNotes"
                    value={invoice.freelancer_notes}
                    disabled
                    readOnly
                  />
                </Larger>
              )}
              <FormField<FormDataT>
                id="comment"
                type="textarea"
                name="comment"
                control={control}
                label="Comments"
                placeholder="Add comments"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Update
            </Button>
            <Button
              color="secondary"
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                onClose();
              }}
            >
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </StyledModal>
  );
};

export default InvoicesEditModal;
